import React, { Suspense, lazy, memo, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { isEmpty } from "../helpers/Functions";
import LoginInit from "./LoginInit";
import { setAppVersion } from "./redux/slices/appSlice";
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from "@mui/material";

const Layout = lazy(() => import("../theme/layout/Layout"));
const SplashScreen = lazy(() => import("./SplashScreen"));
const QuickAccessPage = lazy(() => import("./QuickAccessPage"));

const Login = lazy(() => import("./Login"));
const ErrorPage = lazy(() => import("./ErrorPage"));
const ResetPassword = lazy(() => import("./ResetPassword"));
const LibraryAccessApproval = lazy(() => import("./LibraryAccessApproval"));
const DashboardRoutes = lazy(() => import("./dashboard/DashboardRoutes"));
const AdminPanelRoutes = lazy(() => import("./admin-panel/AdminPanelRoutes"));
const CertificatePage = lazy(() => import("./CertificatePage"));
const WaitingArea = lazy(() => import("./WaitingArea"));
const ZoomRoom = lazy(() => import("./ZoomRoom"));
const HostArea = lazy(() => import("./HostArea"));
const ReviewPage = lazy(() => import("./ReviewPage"));
const EventPortalPage = lazy(() => import("./EventPortalPage"));
const GPTLogin = lazy(() => import("./GPTLogin"));
const CustomEmailApproval = lazy(() => import("./CustomEmailApproval"));
const CourseReferralPage = lazy(() => import("./CourseReferralPage"));
const LiveLessonRoom = lazy(() => import("./LiveLessonsRoom"));
const QuickRegisterPage = lazy(() => import("./QuickRegisterPage"));

export const AppRoutes = memo(() => {
	const isAuthorized = useSelector(({ auth }) => !isEmpty(auth.token));
	const isSuperAdmin = useSelector(({ auth }) => auth.is_super_admin === 1);
	const appVersion = useSelector(({ app }) => app.app_version);
	const navigate = useNavigate();
	
	useEffect(() => {
		if (process.env.REACT_APP_VERSION !== appVersion && window.location.pathname !== "/login") {
			localStorage.clear();
			setAppVersion(process.env.REACT_APP_VERSION);

			if (window.location.pathname === "/dashboard" || window.location.pathname === "/admin-panel") {
				navigate("/login");
				window.location.reload();
			}

			return false;
		}
	}, []);

	// const [updateDialog, setUpdateDialog] = useState({
	// 	open: false,
	// 	isMajorUpdate: false,
	// 	newVersion: ""
	// });

	// useEffect(() => {
	// 	const envVersion = process.env.REACT_APP_VERSION;
	// 	console.log("Environment Version:", envVersion);
		
	// 	if (envVersion !== appVersion && window.location.pathname !== "/login") {
	// 		const [envMajor, envMinor] = envVersion.split('.').map(Number);
	// 		const [appMajor, appMinor] = appVersion.split('.').map(Number);
			
	// 		// Major or minor version change: clear storage and logout
	// 		if (envMajor !== appMajor || envMinor !== appMinor) {
	// 			setUpdateDialog({
	// 				open: true,
	// 				isMajorUpdate: true,
	// 				newVersion: envVersion
	// 			});
	// 		} 
	// 		// Patch version change: just hard refresh
	// 		else {
	// 			setUpdateDialog({
	// 				open: true,
	// 				isMajorUpdate: false,
	// 				newVersion: envVersion
	// 			});
	// 		}
	// 	}
	// }, []);

	// const handleReload = () => {
	// 	if (updateDialog.isMajorUpdate) {
	// 		localStorage.clear();
	// 		setAppVersion(updateDialog.newVersion);
	// 		navigate("/login");
	// 	}
	// 	window.location.reload();
	// };

	return (
		<Suspense fallback={<SplashScreen />}>
			{/* <Dialog
				open={updateDialog.open}
				disableEscapeKeyDown
				aria-labelledby="update-dialog-title"
				aria-describedby="update-dialog-description"
			>
				<DialogTitle id="update-dialog-title">
					New Version Available
				</DialogTitle>
				<DialogContent>
					<DialogContentText id="update-dialog-description">
						{updateDialog.isMajorUpdate ? (
							<>
								A new major version (v{updateDialog.newVersion}) of the application is available. 
								To ensure the best experience, you need to reload the page. 
								<strong> You will be logged out and redirected to the login page.</strong>
							</>
						) : (
							<>
								A new version (v{updateDialog.newVersion}) of the application is available. 
								Please reload the page to get the latest updates and improvements.
							</>
						)}
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleReload} variant="contained" color="primary" autoFocus>
						Reload Now
					</Button>
				</DialogActions>
			</Dialog> */}
			
			<Routes>
				<Route path="/" element={<Navigate to="/dashboard/ai-chat" />} />
				<Route path="/error" element={<ErrorPage />} />
				<Route path="/login" element={<Login />} />
				<Route path="/gpt-login/:library_code" element={<GPTLogin />} />
				<Route path="/reset-password" element={<ResetPassword />} />
				<Route path="/reset-password/:email/:email_token" element={<ResetPassword />} />
				<Route
					path="/approve-library-access-from-email/:account_party_id/:library_party_id/:credit_request"
					element={<LibraryAccessApproval />}
				/>
				<Route path="/me" element={<QuickAccessPage />} />
				<Route path="/feedback" element={<ReviewPage company={"olern"} />} />
				<Route path="/tarkie" element={<ReviewPage company={"tarkie"} />} />
				<Route path="/events/:library_code/:access_code" element={<WaitingArea />} />
				<Route path="/events/" element={<EventPortalPage />} />
				<Route path="/me/:access_code" element={<QuickAccessPage />} />
				<Route path="/certificate/:user_certificate_id/" element={<CertificatePage />} />
				<Route path="/zoom_room/:role" element={<ZoomRoom />} />
				<Route path="/custom_email/:custom_email_id" element={<CustomEmailApproval />} />
				<Route path="/events/host_room/:tab/:library_code" element={<HostArea />} />
				<Route path="/referral/:library_code" element={<CourseReferralPage />} />
				<Route path="/live-lesson/:library_code/:email/" element={<LiveLessonRoom />} />
				<Route path="/quick-register/:library_code/" element={<QuickRegisterPage />} />

				{!isAuthorized &&
					["dashboard/*", "admin-panel/*"].map((path, i) => (
						<Route key={i} path={path} element={<Navigate to="/login" />} />
					))
				}

				<Route path="/init" element={<LoginInit />} />

				<Route element={<Layout />}>
					<Route path="/dashboard/*" element={<DashboardRoutes />} />
					{isSuperAdmin && <Route path="/admin-panel/*" element={<AdminPanelRoutes />} />}
				</Route>

				<Route path="*" element={<Navigate to="/error" />} />
			</Routes>
		</Suspense>
	);
});
